/**
 * BlockRenderer — unified dispatcher for all block types.
 * Renders leaf blocks directly and layout blocks via inline dispatch.
 * No lazy imports, no circular dependencies.
 */
import type { CardRow, CardMeta, BlockType } from "../../../data/types";
import { getBlockVariant, LAYOUT_BLOCK_TYPES } from "../../../data/types";
import LeafBlockRenderer from "./LeafBlockRenderer";
import LayoutBlockRenderer from "./LayoutBlockRenderer";

interface BlockRendererProps {
  row: CardRow;
  blockType: BlockType | null;
  cardMeta: CardMeta;
  readOnly: boolean;
  onUpdate: (row: CardRow) => void;
  resolveAsset?: (src: string) => string;
  dark?: boolean;
}

export default function BlockRenderer({ row, blockType, cardMeta, readOnly, onUpdate, resolveAsset, dark }: BlockRendererProps) {
  if (!blockType) {
    return <div className={`py-2 px-4 text-[0.85rem] rounded-xl ${dark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-400"}`}>Unknown block</div>;
  }

  if (LAYOUT_BLOCK_TYPES.includes(blockType as any)) {
    const variant = getBlockVariant(blockType, cardMeta);
    return (
      <LayoutBlockRenderer
        blockType={blockType}
        content={row[blockType]}
        variant={variant}
        readOnly={readOnly}
        onUpdate={(content: any) => onUpdate({ [blockType]: content })}
        resolveAsset={resolveAsset}
        dark={dark}
        cardMeta={cardMeta}
      />
    );
  }

  return (
    <LeafBlockRenderer
      blockType={blockType}
      content={row[blockType]}
      readOnly={readOnly}
      onUpdate={(content: any) => onUpdate({ [blockType]: content })}
      resolveAsset={resolveAsset}
      dark={dark}
    />
  );
}
